import { Account } from '@adtrackify/at-tracking-event-types';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { showResumeToast } from '../helpers/show-resume-toast';
import { resolveIncompleteSignupDestination } from '@/features/signup/helpers/resolve-incomplete-signup-destination';
import { INCOMPLETE_SIGNUP_DESTINATION_REASON } from '@/features/signup/types/resolve-incomplete-signup-destination-enums';
import { getResumeRoute } from '@/features/signup/helpers/get-resume-route';
import { findEffectivelyActiveAccount, isAccountEffectivelyActive } from '@/lib/helpers/account/account';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import { clearStoredProgress, useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import { resolvePostAuthDestination } from '@/lib/navigation/post-auth-destination';


interface PostLoginRouterReturn {
  routeAfterLogin: (accounts: Account[], userId: string, email: string) => void;
}

function navigateIncompleteDestination(
  incompleteDestination: ReturnType<typeof resolveIncompleteSignupDestination>,
  options: {
    userId: string;
    trimmedEmail: string;
    planId: string | null;
    navigate: ReturnType<typeof useNavigate>;
    searchParams: URLSearchParams;
    setCurrentAccount: (account: Account) => void;
  }
): void {
  const { userId, trimmedEmail, navigate, searchParams, setCurrentAccount } = options;
  const store = useSignupFlowStore.getState();

  if (incompleteDestination.account) {
    setCurrentAccount(incompleteDestination.account);
  }

  switch (incompleteDestination.reason) {
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.NO_ACCOUNTS:
      if (store.completedStep < 2) store.setCompletedStep(2);
      void navigate(incompleteDestination.redirectTo);
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.STORED_PROGRESS: {
      const storedProgress = incompleteDestination.storedProgress;
      if (!storedProgress) {
        void navigate('/signup/company');
        return;
      }

      store.restoreFromLocalStorage(storedProgress);
      store.setEmail(trimmedEmail);
      showResumeToast(trimmedEmail, () => {
        clearStoredProgress(storedProgress.accountId);
        store.reset();
        store.setCompletedStep(2);
        void navigate('/signup/company');
      });
      void navigate(getResumeRoute(storedProgress.completedStep));
      return;
    }

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.MULTIPLE_INCOMPLETE_ACCOUNTS:
      void navigate('/signup/resume-error', {
        state: { error: incompleteDestination.resumeError },
      });
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.CANCELLED_SUBSCRIPTION:
      store.setUserId(userId);
      void navigate(searchParams.get('redirect') || incompleteDestination.redirectTo);
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.RETURN_URL:
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.RESUME_STEP:
      store.setUserId(userId);
      void navigate(incompleteDestination.redirectTo);
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.PLAN_PAYMENT:
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.DEFAULT_PLANS:
      store.setUserId(userId);
      if (incompleteDestination.completedStepToSet !== undefined) {
        store.setCompletedStep(incompleteDestination.completedStepToSet);
      }
      void navigate(incompleteDestination.redirectTo);
      return;
  }
}

export function usePostLoginRouter(
  planId: string | null,
  resetSignupFlow: () => void,
  setIsCheckingSubscription: (v: boolean) => void,
  setCurrentAccount: (account: Account) => void,
): PostLoginRouterReturn {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const routeAfterLogin = useCallback(
    (accounts: Account[], userId: string, trimmedEmail: string) => {
      const lastAccountId = AccountStorage.getLastAccountId();
      const lastUsedAccount = lastAccountId
        ? accounts.find(account => account.id === lastAccountId)
        : undefined;

      // Prefer last-used / currentAccount even when incomplete or cancelled.
      if (lastUsedAccount) {
        setCurrentAccount(lastUsedAccount);

        if (isAccountEffectivelyActive(lastUsedAccount)) {
          if (planId) resetSignupFlow();
          clearStoredProgress(lastUsedAccount.id);
          setIsCheckingSubscription(false);
          void navigate(resolvePostAuthDestination(searchParams.get('redirect')));
          return;
        }

        const store = useSignupFlowStore.getState();
        const incompleteDestination = resolveIncompleteSignupDestination(accounts, {
          userId,
          completedStep: store.completedStep,
          planId: planId ?? undefined,
          returnUrl: searchParams.get('returnUrl') ?? undefined,
          selectedAccountId: lastUsedAccount.id,
        });

        setIsCheckingSubscription(false);
        navigateIncompleteDestination(incompleteDestination, {
          userId,
          trimmedEmail,
          planId,
          navigate,
          searchParams,
          setCurrentAccount,
        });
        return;
      }

      const activeAccount = findEffectivelyActiveAccount(accounts);
      if (activeAccount) {
        setCurrentAccount(activeAccount);
        if (planId) resetSignupFlow();
        clearStoredProgress(activeAccount.id);
        setIsCheckingSubscription(false);
        void navigate(resolvePostAuthDestination(searchParams.get('redirect')));
        return;
      }

      const store = useSignupFlowStore.getState();
      const incompleteDestination = resolveIncompleteSignupDestination(accounts, {
        userId,
        completedStep: store.completedStep,
        planId: planId ?? undefined,
        returnUrl: searchParams.get('returnUrl') ?? undefined,
      });

      setIsCheckingSubscription(false);
      navigateIncompleteDestination(incompleteDestination, {
        userId,
        trimmedEmail,
        planId,
        navigate,
        searchParams,
        setCurrentAccount,
      });
    },
    [navigate, searchParams, planId, resetSignupFlow, setIsCheckingSubscription, setCurrentAccount]
  );

  return { routeAfterLogin };
}
